import { ColumnDetails } from '../../../grid/types/common';
import { AccountRowDataGrid, ColumnIndexes, ColumnKeys, serviceTypes } from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { GzLinkWebComponent } from '../../../ui/gzLinkComponent';
import { LinkType } from '../../../ui/gzLinkComponent/types';
import { FontSizes } from 'apps/webcomponents/src/types/common/types';
import { AccountSource } from '../../common/types/AccountSources';
import { html, TemplateResult } from 'lit';

export function fullNameRenderer(
  rowData: AccountRowDataGrid,
  columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>
): TemplateResult {
  const profile = rowData.item['profile'];
  if (!profile) {
    return translateFn('N/A');
  }

  let fullName = profile[columnDetails.key];

  if (!fullName) {
    fullName = translateFn('N/A');
  }

  const accountData = rowData.item;
  if (accountData.hasOwnProperty('readOnly') && accountData.readOnly) {
    return html`${fullName}`;
  }

  return html`
    <gz-link type=${LinkType.QUIET}
             fontSize=${FontSizes.PARAGRAPH_12}
             @click="${() => this.onFullNameClick(rowData.item)}"
    >${fullName}</gz-link>`;
}

export function userNameRenderer(
  rowData: AccountRowDataGrid,
  columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>
): TemplateResult {
  let username = rowData.item[columnDetails.key];

  if (!username) {
    return translateFn('N/A');
  }

  const accountData = rowData.item;

  if (accountData.readOnly) {
    return html`${username}`;
  }

  return html`
    <gz-link type=${LinkType.QUIET}
             fontSize=${FontSizes.PARAGRAPH_12}
             @click="${() => this.onUsernameClick(rowData.item)}"
    >${username}</gz-link>`;
}

export function roleRenderer(
  rowData: AccountRowDataGrid,
  columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>
): TemplateResult {
  let role = rowData.item[columnDetails.key];
  if (role && role.name) {
    return html`${role.name}`;
  }

  return translateFn('N/A');
}

export function companyRenderer(
  rowData: AccountRowDataGrid,
  columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>
): TemplateResult {
  let company = rowData.item[columnDetails.key];
  if (company && company.name) {
    return html`${company.name}`;
  }
  return translateFn('N/A');
}

export function twoFARenderer(
  rowData: AccountRowDataGrid,
  columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>
): string {
  const twoFactor = rowData.item[columnDetails.key];

  if (twoFactor && twoFactor.hasOwnProperty('enabled') && twoFactor.enabled) {
    return translateFn('Enabled');
  }

  return translateFn('Disabled');
}

export function passwordExpirationRenderer(
  rowData: AccountRowDataGrid,
  columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>
): string {
  const passwordLifetime = rowData.item[columnDetails.key];

  if (passwordLifetime && passwordLifetime > 0) {
    return translateFn('Enabled');
  }

  return translateFn('Disabled');
}

export function accountLockoutRenderer(
  rowData: AccountRowDataGrid,
  columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>
): string {
  const accountLockout = rowData.item[columnDetails.key];

  if (accountLockout && accountLockout.hasOwnProperty('enabled') && accountLockout.enabled) {
    return translateFn('Enabled');
  }

  return translateFn('Disabled');
}

export function accessRuleRenderer(
  rowData: AccountRowDataGrid,
  columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>
): TemplateResult {
  const securityGroupRole = rowData.item[columnDetails.key];

  if (!securityGroupRole) {
    return translateFn('N/A');
  }

  if (securityGroupRole.length !== undefined && securityGroupRole.length === 0) {
    return translateFn('N/A');
  }

  if (!securityGroupRole.name) {
    return translateFn('N/A');
  }

  return html`
    <gz-link type=${LinkType.QUIET}
             fontSize=${FontSizes.PARAGRAPH_12}
             @click="${() => this.onClickOnRuleName(securityGroupRole)}"
    >${securityGroupRole.name}</gz-link>`;
}

export function servicesRenderer(
  rowData: AccountRowDataGrid,
  columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>
): string {
  const serviceTargets = rowData.item[columnDetails.key];

  let outputString = '';

  if (serviceTargets.length === 0) {
    return translateFn('N/A');
  }

  const serviceIdArray = Array<Number>();
  for (const service of serviceTargets) {
    serviceIdArray.push(service.serviceId);
  }
  serviceIdArray.sort();

  const services = {
    [serviceTypes.EPS]: translateFn('Computers'),
    [serviceTypes.SVE]: translateFn('Cloud Workloads'),
    [serviceTypes.MDM]: translateFn('Mobile Devices'),
  };
  for (const serviceId of serviceIdArray) {
    if (outputString === '') {
      outputString += services[serviceId + ''];
    } else {
      outputString += ', ' + services[serviceId + ''];
    }
  }

  return outputString;
}

export function statusRenderer(rowData, columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>) {
  if (!this.dependencies.isCloud()) {
    let statuses: Array<string> = [];
    if (rowData.item.source === AccountSource.ACTIVEDIR && !rowData.item.sourceSpecifics.connected) {
      statuses.push(translateFn('AD integration removed'));
    }

    if (!rowData.item.active) {
      statuses.push(translateFn('Suspended (User deactivated and removed from access rule)'));
    }

    if (statuses.length) {
      const allStatuses = statuses.map((status, index) => {
        if (index === 0) {
          return html`<span class="cell-label">${status}</span>`;
        }
        return html`<span class="cell-label" style="margin-top: 6px">${status}</span>`;
      });

      return html` <div class="account-statuses" style="display: grid">${allStatuses}</div>`;
    }

    return translateFn('Active');
  }

  if (!rowData.item.active) {
    return html`<span class="cell-label"
      >${translateFn('Suspended (User deactivated due to suspended company)')}</span
    >`;
  }

  return translateFn('Active');
}
